import * as React from "react";
import { styled } from "@linaria/react";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import Demo, { DemoSliderControl } from "@/components/Demo";
import FakeBrowserWindow from "@/components/FakeBrowserWindow";

const CONTENT =
  "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since 1966, when designers at Letraset and James Mosley, the librarian at St Bride Printing Library in London, took a 1914 Cicero translation and scrambled it to make dummy text for Letraset's Body Type sheets.";

const WORDS = CONTENT.split(" ");

function WidthVsHeightDemo() {
  const contentBoxRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState(0);

  React.useEffect(() => {
    if (!contentBoxRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.borderBoxSize[0].blockSize);
      }
    });

    resizeObserver.observe(contentBoxRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Demo
      initialValues={{
        numOfWords: 20,
      }}
      outputWrapper={null}
      controlPosition="bottom"
      customGrid={{
        gridTemplateColumns: "min(400px, 100%)",
        justifyContent: "center",
      }}
      controls={[
        <DemoSliderControl
          key="numOfWords"
          id="numOfWords"
          label="Number of Words"
          min={0}
          max={WORDS.length}
        />,
      ]}
    >
      {({ numOfWords }) => {
        const content = WORDS.slice(0, numOfWords).join(" ");

        return (
          <>
            <Container>
              {contentHeight > 0 && (
                <HeightArrowWrapper style={{ height: contentHeight }}>
                  <HeightArrowLabel>
                    Minimum
                    <br />
                    Height
                  </HeightArrowLabel>
                  <HeightArrow>
                    <Up strokeWidth={3} />
                    <VerticalShaft />
                    <Down strokeWidth={3} />
                  </HeightArrow>
                </HeightArrowWrapper>
              )}
              <StyledBrowserWindow colorModeOverride="dark">
                <FakeWindowContent>
                  <ContentBox ref={contentBoxRef}>
                    <p>{content}</p>
                    {contentHeight > 0 && (
                      <WidthArrowWrapper>
                        <WidthArrow>
                          <Left size={16} strokeWidth={3} />
                          <HorizontalShaft />
                          <Right size={16} strokeWidth={3} />
                        </WidthArrow>
                        <WidthArrowLabel>Maximum Width</WidthArrowLabel>
                      </WidthArrowWrapper>
                    )}
                  </ContentBox>
                </FakeWindowContent>
              </StyledBrowserWindow>
            </Container>
          </>
        );
      }}
    </Demo>
  );
}

const Container = styled.div`
  position: relative;
  margin-inline: auto;
  width: 500px;
  max-width: 100%;
  margin-bottom: 24px;

  @media (max-width: 40.5rem) {
    width: revert;
    max-width: revert;
    margin-inline: revert;
    margin-left: 3rem;
  }
`;

const StyledBrowserWindow = styled(FakeBrowserWindow)`
  width: 100%;
`;

const FakeWindowContent = styled.div`
  height: 20rem;
  padding: 8px;
  background: white;
  color: black;
`;

const ContentBox = styled.div`
  position: relative;
  background: hsl(270deg 100% 85%);
  border-radius: 4px;
  padding: 1rem;

  p {
    font-size: 0.875rem;
    text-wrap: revert;
    max-height: 220px;
    overflow: hidden;
  }
`;

const HeightArrowWrapper = styled.div`
  position: absolute;
  top: 81px;
  left: -8px;
  bottom: 0;
  display: flex;
  color: var(--color-gray-900);
  transform: translateX(-100%);
  animation: fadeFromTransparent 300ms 300ms backwards;
`;

const HeightArrow = styled.div`
  position: relative;
  width: 16px;
`;

const ArrowLabel = styled.span`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
`;

const HeightArrowLabel = styled(ArrowLabel)`
  align-self: center;
  text-align: right;
  padding-right: 8px;
  line-height: 1.2;
  transition: transform 300ms;

  @media (max-width: 44.5rem) {
    transform-origin: center center;
    transform: translateX(12px) rotate(-90deg);
  }
`;

const WidthArrowWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -8px;
  transform: translateY(100%);
  color: var(--color-gray-300);
  animation: fadeFromTransparent 300ms 300ms backwards;
`;

const WidthArrow = styled.div`
  position: relative;
  height: 16px;
`;

const WidthArrowLabel = styled(ArrowLabel)`
  display: flex;
  justify-content: center;
  text-align: center;
`;

const Up = styled(ChevronUp)`
  position: absolute;
  top: -9px;
`;
const Down = styled(ChevronDown)`
  position: absolute;
  bottom: -9px;
`;
const Left = styled(ChevronLeft)`
  position: absolute;
  left: -4px;
`;
const Right = styled(ChevronRight)`
  position: absolute;
  right: -4px;
`;

const Shaft = styled.div`
  position: absolute;
  margin: auto;
  background: var(--color-gray-900);
  border-radius: 10px;
`;

const VerticalShaft = styled(Shaft)`
  top: 2px;
  bottom: 2px;
  left: 0;
  right: 0;
  width: 2px;
`;
const HorizontalShaft = styled(Shaft)`
  top: 0;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 2px;
  background: var(--color-gray-300);
`;

export default WidthVsHeightDemo;
